import dayjs from "dayjs";
import ReactDOMServer from "react-dom/server";

import { watermarkService } from "app/common/services/watermarkService";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noAnimationConfig from "pro-features/components/Chart/src/configs/animation/noAnimationConfig";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";
import { getGoogleAiModeAnnotationPlotLines } from "pro-features/components/Chart/src/configs/plotLines/googleAiModeAnnotations";
import type { TimeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import { minAbbrNumberFilter } from "app/filters/ngFilters";

import { TrafficShareGraphTooltip } from "../components/GraphTooltip";

export const getChartConfig = (
    timeGranularity: TimeGranularity,
    translate: (key: string) => string,
    displayedPeriods: readonly number[] = [],
) => {
    const isDailyGranularity = timeGranularity === "Daily";
    const xAxisFormat = isDailyGranularity ? "DD MMM" : "MMM YY";

    const yAxisFormatter = ({ value }) => minAbbrNumberFilter()(value);
    const xAxisFormatter = ({ value }) => dayjs.utc(value).format(xAxisFormat);

    return combineConfigs({ yAxisFormatter, xAxisFormatter }, [
        noLegendConfig,
        xAxisLabelsConfig,
        yAxisLabelsConfig,
        xAxisCrosshair,
        noAnimationConfig,
        tooltipContainerConfig,
        {
            chart: {
                type: "line",
                spacingTop: 10,
                plotBackgroundColor: "transparent",
                style: {
                    fontFamily: "Roboto",
                },
                events: {
                    load: function () {
                        watermarkService.add.call(this, { opacity: 0.1 });
                    },
                },
            },
            plotOptions: {
                line: {
                    lineWidth: 2,
                    connectNulls: true,
                },
                series: {
                    marker: {
                        enabled: true,
                        symbol: "circle",
                    },
                },
            },
            yAxis: {
                gridLineWidth: 0.5,
                showFirstLabel: false,
                showLastLabel: true,
                reversed: false,
                gridZIndex: 2,
                tickPixelInterval: 50,
                plotLines: [
                    {
                        color: "#656565",
                        width: 2,
                        value: 0,
                    },
                ],
                labels: {
                    style: {
                        textTransform: "uppercase",
                        fontSize: "11px",
                        fontFamily: "Roboto",
                    },
                },
            },
            xAxis: {
                gridLineWidth: 0,
                gridLineDashStyle: "dash",
                tickLength: 5,
                alignTicks: true,
                labels: {
                    style: {
                        textTransform: "capitalize",
                        fontSize: "11px",
                        color: "#919191",
                        fontFamily: "Roboto",
                    },
                },
                minPadding: 0,
                maxPadding: 0,
                type: "datetime",
                plotLines: getGoogleAiModeAnnotationPlotLines(displayedPeriods, timeGranularity),
            },
            tooltip: {
                shared: true,
                // rendered outside the 366px chart box so the annotation banner is not clipped
                outside: true,
                useHTML: true,
                formatter: function () {
                    const { points } = this;

                    const tooltipHeaders = {
                        domain: translate("common.domain"),
                        traffic: translate("analysis.marketing.channels.traffic.sources.chart.tooltip.traffic"),
                    };

                    return ReactDOMServer.renderToString(
                        <TrafficShareGraphTooltip
                            points={points}
                            timeGranularity={timeGranularity}
                            tooltipHeaders={tooltipHeaders}
                        />,
                    );
                },
            },
        },
    ]);
};
